import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Typography } from 'src/components/Typography';

import { FilterTypeGroup } from './FilterTypeGroup';
import { FilterTypeLifecycle } from './FilterTypeLifecycle';
import { FilterTypeLocation } from './FilterTypeLocation';
import { FilterTypePurchase } from './FilterTypePurchase';
import { FilterTypeSubscription } from './FilterTypeSubscription';
import { FilterTypeTag } from './FilterTypeTag';
import { FilterGroup, FilterNode, FilterSetProps } from './types';

const FilterTypeMap = {
  group: FilterTypeGroup,
  location: FilterTypeLocation,
  tag: FilterTypeTag,
  lifecycle: FilterTypeLifecycle,
  subscription: FilterTypeSubscription,
  purchase: FilterTypePurchase,
};

export const FilterSet: FC<FilterSetProps> = ({
  filter,
  parent,
  root,
  onChangeRoot,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'reminderFilters.filterSet',
  });

  const notify = () => onChangeRoot({ ...root });

  const deleteFilter = (target: FilterNode | FilterGroup) => {
    if (!parent) return;

    const idx = parent.filters.indexOf(target);
    if (idx === -1) return;

    parent.filters.splice(idx, 1);

    if (parent.filters.length === 0 && parent !== root) {
      deleteFromParent(parent, root);
    } else if (
      parent.filters.length === 1 &&
      'condition' in parent.filters[0]
    ) {
      const child = parent.filters[0] as FilterGroup;
      const grandParent = findParent(parent, root);
      if (grandParent) {
        const pIdx = grandParent.filters.indexOf(parent);
        grandParent.filters.splice(pIdx, 1, child);
      } else {
        Object.assign(root, child);
      }
    }
    notify();
  };

  if ('condition' in filter) {
    const group = filter as FilterGroup;
    return (
      <Box pl={2} borderLeft="1px solid #e0e0e0" mt={1}>
        {group.filters.map((sub, idx) => (
          <Box key={idx} display="flex" alignItems="center">
            {idx > 0 && (
              <Typography
                variant="body2"
                sx={{ px: 1, fontWeight: 'bold', color: 'primary.main' }}
              >
                {t(group.condition.toLowerCase())}
              </Typography>
            )}
            <FilterSet
              filter={sub}
              parent={group}
              root={root}
              onChangeRoot={onChangeRoot}
            />
          </Box>
        ))}
      </Box>
    );
  }

  const node = filter as FilterNode;
  const FilterComponent = FilterTypeMap[node.type];
  return (
    <Box display="flex" alignItems="center" gap={1} my={0.5}>
      {FilterComponent && (
        <FilterComponent
          value={node}
          onChange={(v) => {
            Object.assign(node, v);
            notify();
          }}
          onDelete={() => deleteFilter(node)}
        />
      )}
    </Box>
  );
};

const deleteFromParent = (
  node: FilterNode | FilterGroup,
  root: FilterGroup
) => {
  const parent = findParent(node, root);
  if (parent) {
    const idx = parent.filters.indexOf(node);
    if (idx !== -1) parent.filters.splice(idx, 1);
  }
};

const findParent = (
  node: FilterNode | FilterGroup,
  root: FilterGroup
): FilterGroup | null => {
  const stack: FilterGroup[] = [root];
  while (stack.length) {
    const current = stack.pop()!;
    for (const child of current.filters) {
      if (child === node) return current;
      if ('condition' in child) stack.push(child as FilterGroup);
    }
  }
  return null;
};
